/**
 * Menus: sections of recipe/ingredient lines, costed live and snapshotted
 * for drift detection. Ports legacy /menus* (docs/parity/menus.md) against
 * the FastAPI router at backend/pleach/api/menus.py.
 *
 * Every computed figure (cost, cogs%, margin, both food-only and
 * food+labour, cost_complete, drift) comes back pre-computed on
 * MenuDishOut/MenuListItemOut/MenuDetailOut. This file never recomputes
 * engine maths: it only fetches, caches, invalidates and drives the live
 * "type a qty, see it recompute" preview.
 *
 * Beta lock: every endpoint here 403s feature_locked for a non-admin while
 * Menus is locked. Screens gate themselves first with useFeatureGate('menus')
 * (see components/ComingSoon.tsx) so these hooks are never even called for a
 * locked viewer; they do not special-case that error themselves.
 *
 * Restore/purge for a soft-deleted menu live on the Trash domain's router,
 * not here (src/api/queries/trash.ts's useRestoreMenu/usePurgeMenu). This
 * file re-exports nothing extra for that: screens import trash's hooks
 * directly and invalidate menusKeys.all from here after a live event either
 * way (registerLive('menu.', ...) below runs regardless of which screen
 * triggered the change).
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { isMine, registerLive } from '@/lib/live';

export type MenuListItem = components['schemas']['MenuListItemOut'];
export type MenuSectionSummary = components['schemas']['MenuSectionSummaryOut'];
export type MenuDetail = components['schemas']['MenuDetailOut'];
export type MenuSection = components['schemas']['MenuSectionOut'];
export type MenuDish = components['schemas']['MenuDishOut'];
export type MenuDishDrift = components['schemas']['MenuDishDrift'];
export type MenuSynergy = components['schemas']['MenuSynergyOut'];
export type MenuSynergySummary = components['schemas']['MenuSynergySummaryOut'];
export type IncompleteSummary = components['schemas']['IncompleteSummaryOut'];
export type MenuStats = components['schemas']['MenuStatsOut'];
export type MenuPrint = components['schemas']['MenuPrintOut'];
export type MenuCreateIn = components['schemas']['MenuCreateIn'];
export type MenuUpdateIn = components['schemas']['MenuUpdateIn'];
export type MenuPreviewIn = components['schemas']['MenuPreviewIn'];
export type MenuSectionIn = components['schemas']['MenuSectionIn'];
export type MenuDishIn = components['schemas']['MenuDishIn'];
export type SearchResult = components['schemas']['SearchResultOut'];
export type SynergyBand = MenuSynergy['band'];

const DOMAIN = 'menus' as const;

export const menusKeys = {
  all: [DOMAIN] as const,
  list: (includeDeleted: boolean) => [DOMAIN, 'list', includeDeleted] as const,
  detail: (id: number) => [DOMAIN, 'detail', id] as const,
  print: (id: number, opts: PrintOptions) => [DOMAIN, 'print', id, opts] as const,
};

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: menusKeys.all });
}

// ── List / detail ────────────────────────────────────────────────────────

function listQuery(includeDeleted: boolean) {
  return queryOptions({
    queryKey: menusKeys.list(includeDeleted),
    queryFn: () => unwrap(api.GET('/api/menus', { params: { query: { include_deleted: includeDeleted || undefined } } })),
  });
}

/** The list screen fetches once with include_deleted so it can build its own
 *  live-menus grid plus a "Recently Deleted" panel from one response, same
 *  round trip shape ingredients.ts uses for its own trash panel. */
export function useMenusList(includeDeleted: boolean) {
  return useQuery(listQuery(includeDeleted));
}

/** Exported separately (not just inlined in useMenu) so a route loader can
 *  ensureQueryData() the exact same query the screen then reads via
 *  useMenu/useQuery, same pattern as ingredients.ts's ingredientDetailQuery. */
export function menuDetailQuery(id: number, includeDeleted = false) {
  return queryOptions({
    queryKey: menusKeys.detail(id),
    queryFn: () =>
      unwrap(
        api.GET('/api/menus/{menu_id}', {
          params: { path: { menu_id: id }, query: { include_deleted: includeDeleted || undefined } },
        }),
      ),
  });
}

export function useMenu(id: number | undefined, opts?: { includeDeleted?: boolean; enabled?: boolean }) {
  return useQuery({
    ...menuDetailQuery(id ?? -1, opts?.includeDeleted),
    enabled: id !== undefined && (opts?.enabled ?? true),
  });
}

/** When a menu last changed, for the "updated 14 Sep" meta: its last saved
 *  change, else when it was created (a menu never edited has no
 *  updated_at). The list and the detail both send the two. */
export function menuChangedAt(m: Pick<MenuListItem, 'created_at' | 'updated_at'>): { word: 'updated' | 'created'; at: string | null } {
  if (m.updated_at) return { word: 'updated', at: m.updated_at };
  return { word: 'created', at: m.created_at };
}

// ── Mutations ────────────────────────────────────────────────────────────

export function useCreateMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MenuCreateIn) => unwrap(api.POST('/api/menus', { body })),
    onSuccess: (data) => {
      qc.setQueryData(menusKeys.detail(data.id), data);
      invalidateAll(qc);
    },
  });
}

/** "New menu": the handoff's one step. Creates "Untitled menu" with an
 *  empty "Mains" section and answers with the saved menu, so the builder
 *  opens on a clean draft with the name ready to type over. */
export function useStartMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const created = await unwrap(api.POST('/api/menus', { body: { name: 'Untitled menu', target_cogs: null } }));
      try {
        return await unwrap(
          api.PUT('/api/menus/{menu_id}', {
            params: { path: { menu_id: created.id } },
            body: { version: created.version, name: created.name, target_cogs: null, sections: [{ name: 'Mains', items: [] }] },
          }),
        );
      } catch {
        // The menu exists; the builder's "Add section" covers a missing Mains.
        return created;
      }
    },
    onSuccess: (data) => {
      qc.setQueryData(menusKeys.detail(data.id), data);
      invalidateAll(qc);
    },
  });
}

/** Aggregate save: the whole name/target/sections/dishes tree in one
 *  version-checked PUT. On 409 the caller opens ConflictDialog via
 *  useConflict() instead of treating this as a generic error. */
export function useUpdateMenu(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MenuUpdateIn) => unwrap(api.PUT('/api/menus/{menu_id}', { params: { path: { menu_id: id } }, body })),
    onSuccess: (data) => {
      qc.setQueryData(menusKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

export function useDeleteMenu(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      unwrap(api.DELETE('/api/menus/{menu_id}', { params: { path: { menu_id: id }, query: { version } } })),
    onSuccess: (data) => {
      qc.setQueryData(menusKeys.detail(id), data);
      invalidateAll(qc);
    },
  });
}

/** Live "type a qty, see the cost recompute" preview: applies the same body
 *  shape as the aggregate save in a server-side SAVEPOINT and rolls back:
 *  nothing persists, no version field/check, never conflicts. Debounce
 *  ~250ms client-side (docs/ARCHITECTURE.md) before calling mutate(). */
export function useMenuPreview(id: number) {
  return useMutation({
    mutationFn: (body: MenuPreviewIn) => unwrap(api.POST('/api/menus/{menu_id}/preview', { params: { path: { menu_id: id } }, body })),
  });
}

export function useMenuDishDrift(menuId: number, dishId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [DOMAIN, 'drift', menuId, dishId] as const,
    queryFn: () =>
      unwrap(
        api.GET('/api/menus/{menu_id}/dishes/{dish_id}/drift', {
          params: { path: { menu_id: menuId, dish_id: dishId! } },
        }),
      ),
    enabled: enabled && dishId !== undefined,
  });
}

/** "Pull the latest": re-takes one line's drift signature, clearing its
 *  badge. Also bumps the parent Menu's version server-side, so this
 *  invalidates the menu detail (not just a local row patch): a stale
 *  editor's next Save should 409 rather than silently clobber the just
 *  cleared drift state. */
export function useRefreshMenuDish(menuId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dishId: number) =>
      unwrap(api.POST('/api/menus/{menu_id}/dishes/{dish_id}/refresh', { params: { path: { menu_id: menuId, dish_id: dishId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: menusKeys.detail(menuId) });
      void qc.invalidateQueries({ queryKey: menusKeys.list(false) });
      void qc.invalidateQueries({ queryKey: menusKeys.list(true) });
    },
  });
}

// ── Print ────────────────────────────────────────────────────────────────

export type PrintOptions = { cost: boolean; sell: boolean; margin: boolean; cogs: boolean; pretty: boolean };

export function useMenuPrint(id: number, opts: PrintOptions, includeDeleted: boolean) {
  return useQuery({
    queryKey: menusKeys.print(id, opts),
    queryFn: () =>
      unwrap(
        api.GET('/api/menus/{menu_id}/print', {
          params: {
            path: { menu_id: id },
            query: { ...opts, include_deleted: includeDeleted || undefined },
          },
        }),
      ),
  });
}

// ── Item picker (shared typeahead, GET /api/search) ─────────────────────

/** Both halves (ingredients + recipes) in one call for the "+ Add item"
 *  picker. ingredients.ts's searchIngredients() only asks for the
 *  ingredient half, so this domain calls the endpoint directly rather than
 *  importing a same-shaped-but-filtered helper from another domain. */
export async function searchMenuItems(q: string): Promise<SearchResult[]> {
  if (!q.trim()) return [];
  return unwrap(api.GET('/api/search', { params: { query: { q } } }));
}

// ── Live updates ─────────────────────────────────────────────────────────
// menu.* -> this domain's own lists/details. menu_dish.refreshed carries
// {id, menu_id, version}: refreshing one line bumps the parent menu's
// version, so a second viewer's open detail needs the same invalidation a
// menu.updated would trigger. `isMine` skips the refetch when this tab's
// own mutation already updated the cache in its onSuccess above.
registerLive('menu.', (evt, qc) => {
  if (isMine(evt)) return;
  invalidateAll(qc);
});
registerLive('menu_dish.', (evt, qc) => {
  if (isMine(evt)) return;
  invalidateAll(qc);
});
